/**
 * Centralized Debug Configuration
 *
 * Toggle debugging on/off from this single file.
 * NEVER commit this file with ENABLED = true
 *
 * Usage:
 *   1. Set ENABLED to true locally to see debug logs
 *   2. Filter by LEVEL to control verbosity
 *   3. Filter by FEATURES to focus on specific areas
 */

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

export type LogFeature =
  | 'auth'
  | 'api'
  | 'video'
  | 'audio'
  | 'firebase'
  | 'upload'
  | 'subscription'
  | 'extension'
  | 'ui'
  | 'notes'
  | 'stripe'
  | 'onboarding'
  | 'settings'
  | 'projects'
  | 'secondBrain'
  | 'genesis'
  | 'referral';

export const DEBUG_CONFIG = {
  /**
   * Master switch - set to true locally to enable debug logs
   * WARNING: Never commit with ENABLED = true
   */
  ENABLED: false,

  /**
   * Log level threshold
   * DEBUG = show all logs
   * INFO = show info, warn only (errors use errorReporter service)
   * WARN = show warn only (errors use errorReporter service)
   * Note: For errors, use errorReporter.critical/expected/debug instead of logger
   */
  LEVEL: 'DEBUG' as LogLevel,

  /**
   * Filter logs to specific features only
   * Empty array = show all features
   * e.g., ['auth', 'api'] = only show auth and api logs
   */
  FEATURES: [] as LogFeature[],
} as const;

/**
 * Log level priority for comparison
 */
export const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};
